import { useEffect } from 'react';
import { Link } from 'wouter';
import { usePageMeta } from '../../lib/usePageMeta';
import { routes } from '../../lib/assessment/routes';
import { buttonVariants } from '../ui/button';
import { cn } from '../../lib/utils';
// D1 (approved): `/why` takes its icons from `lucide-react`, the library legacy
// uses, rather than the local `./icons` reproductions. `ExternalLink` is aliased
// because the link focus-ring scanner in test/visualSystem.test.ts treats any
// JSX tag ending in `Link` as a link element; this renders an icon, not a link.
import { ArrowRight, ExternalLink as ExternalLinkIcon } from 'lucide-react';

/**
 * The Why HPOS page, migrated from the legacy application's
 * `src/pages/why.tsx` (V2-018C-2).
 *
 * Parity migration. Copy, headings, section order, images, links, external
 * destinations, CTAs, and the Person structured-data block are reproduced
 * exactly. The same four categories of change apply as on the homepage, and
 * only those four:
 *
 * 1. **V2 routing.** Legacy `/assessment`, `/work-fit/assessment`, and
 *    `/perspective/assessment` become this repository's `/assessment/:type`.
 *    Every external destination is unchanged.
 * 2. **The shell.** The legacy per-page header and footer are the Experience
 *    Shell's concern here.
 * 3. **Obsolete technology.** Clerk's `useUser` drove only the legacy header's
 *    Dashboard/Sign In swap, which the shell now owns.
 * 4. **Accessibility.** Legacy nests `<Button>` inside `<Link>` and inside
 *    `<a>`; here the anchor itself carries the button styling, so rendering is
 *    unchanged and the markup is valid.
 *
 * The founder photograph is served from this repository's `public/`, so no
 * external origin is referenced and the content policy is unchanged. The
 * outbound links to craigwillard.com and Amazon are navigation, not asset
 * loads, and are carried over verbatim.
 */

/** Repeated at each link site rather than hoisted: the visual-system contract
 *  test reads the `className` source text, so a shared constant would satisfy
 *  the runtime and fail the guard. */

export function Why() {
  usePageMeta({
    title: 'Why I Built High Performer OS | Craig Willard',
    description:
      'High Performer OS was built to give high performers a structured first step: identify the pattern, understand what it is costing, and begin practicing a more useful response.',
    canonical: 'https://highperformeros.com/why',
    ogImage: 'https://highperformeros.com/logo-white-v2.webp',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'person-schema-craig';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Craig Willard',
      url: 'https://www.craigwillard.com',
      jobTitle: 'Performance Coach',
      description:
        'Craig Willard is the creator of High Performer OS. He works with high performers, MSP business owners, and leaders on performance, mindset, and execution. He holds a doctorate in Industrial and Organizational Psychology.',
      sameAs: ['https://www.craigwillard.com'],
      knowsAbout: [
        'High Performance',
        'Performance Coaching',
        'Industrial and Organizational Psychology',
        'Mindset',
        'Behavioral Change',
      ],
      creator: {
        '@type': 'SoftwareApplication',
        name: 'High Performer OS',
        url: 'https://highperformeros.com',
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('person-schema-craig');
      if (el) document.head.removeChild(el);
    };
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-4 py-24 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-2 rounded-full mb-8">
          Behind High Performer OS
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
          Why I Built <span className="text-primary">High Performer OS</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
          Thousands of high performers were recognizing themselves in the patterns. The next
          question was always the same: "What do I do now?"
        </p>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <Link
            href={routes.assessment('bottleneck')}
            className={cn(
              buttonVariants({ size: 'lg' }),
              'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(245,184,0,0.25)] gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Start the Free Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="https://www.craigwillard.com"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              buttonVariants({ size: 'lg', variant: 'outline' }),
              'text-base px-8 py-6 h-auto gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Learn More About Craig
            <ExternalLinkIcon className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Section 1: The response that started it */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            The Response That Started It
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            People weren't just saying the content was interesting.
          </h2>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              Craig's TikTok content has covered the patterns that quietly affect high-performing
              professionals: avoidance, overextension, identity pressure, recovery deficits,
              overthinking, burnout, and performance friction. The content reached people who
              recognized themselves in it.
            </p>
            <p>
              The response wasn't curiosity. It was recognition. And recognition almost always came
              with a follow-up question.
            </p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              "That's me.",
              'How do I stop this from happening?',
              'What do I do next?',
              'How do I fix this?',
              'Why do I know better but still keep repeating the pattern?',
            ].map((q) => (
              <div key={q} className="rounded-xl border border-border bg-background px-5 py-4">
                <p className="text-sm font-medium text-foreground">{q}</p>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground leading-relaxed mt-8">
            These are not abstract questions. They are the questions people ask when they have
            enough self-awareness to see the pattern and not yet enough structure to change it.
          </p>
        </div>
      </section>

      {/* Section 2: Recognition is not enough */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            The Problem with Recognition Alone
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            Recognition creates awareness.{' '}
            <span className="text-primary">
              Awareness without a next step often becomes frustration.
            </span>
          </h2>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              Seeing yourself in a pattern is genuinely valuable. It is the beginning of something.
              But recognition without a structured response tends to loop. You see it, you feel it,
              and then the same situation comes back and the same pattern runs.
            </p>
            <p>
              High Performer OS was built to give people the next step after recognition. It is not
              a content platform. It is not a personality quiz. It is a structured starting point:
              identify the specific pattern, understand what it is costing, and begin practicing a
              more useful response.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: From coaching to content to HPOS */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            How It Connects
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-10">
            From coaching to content to High Performer OS
          </h2>
          <div className="space-y-6">
            {[
              {
                label: 'Coaching',
                desc: 'Where it started. Craig worked directly with high performers, business operators, and leaders on the patterns showing up in their work and decisions.',
              },
              {
                label: 'The books',
                desc: 'The frameworks from coaching became the books. The same operating patterns, documented in writing and made available beyond 1-on-1 work.',
              },
              {
                label: 'TikTok',
                desc: 'The content brought the patterns to a wider audience. People who had never worked with a coach started recognizing themselves in what they were watching.',
              },
              {
                label: 'High Performer OS',
                desc: 'The next step after recognition. A structured tool to identify the specific pattern, understand what it costs, and start the reset.',
              },
            ].map(({ label, desc }) => (
              <div key={label} className="flex gap-5 items-start">
                <div className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
                <div>
                  <p className="font-semibold mb-1">{label}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Craig's coaching and body of work */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-10 items-start">
            <div className="shrink-0 flex flex-col items-center md:items-start gap-4">
              <img
                src={`${import.meta.env.BASE_URL}craig-willard.webp`}
                srcSet={`${import.meta.env.BASE_URL}craig-willard@1x.webp 250w, ${import.meta.env.BASE_URL}craig-willard.webp 500w`}
                sizes="208px"
                alt="Craig Willard"
                className="w-52 h-52 object-cover object-top rounded-2xl border border-border"
                loading="lazy"
              />
              <a
                href="https://www.craigwillard.com"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'sm' }),
                  'gap-2 w-full',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                Visit CraigWillard.com
                <ExternalLinkIcon className="w-3.5 h-3.5" />
              </a>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Craig Willard
              </p>
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                Coaching, books, speaking, and frameworks
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Craig Willard works with high performers, MSP business owners, and leaders on
                  performance, mindset, and execution. His work started in 1-on-1 coaching, became
                  books, reached a wider audience through TikTok content, and led to High Performer
                  OS.
                </p>
                <p>
                  Craig holds a doctorate in Industrial and Organizational Psychology. His
                  frameworks are grounded in the psychology of performance, motivation, and
                  behavioral change. The patterns High Performer OS measures are the same ones he
                  has worked with in coaching for years.
                </p>
                <p>
                  CraigWillard.com is the home for that work. High Performer OS is one part of the
                  broader system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Books */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 text-center">
            The Framework Behind the Assessments
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-10 text-center">The books</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-background p-7 space-y-4 flex flex-col">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">
                Mindset and Perspective
              </p>
              <h3 className="text-xl font-bold">The High Performance Mindset</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                This book connects directly to the Perspective work: mindset, self-talk, belief
                testing, conscious and subconscious patterning, time orientation, operating
                statements, and changing practiced patterns. If you want to understand the deeper
                framework behind the Perspective Assessment, this is where it lives.
              </p>
              <a
                href="https://www.amazon.com/dp/1619617323"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                View on Amazon
                <ExternalLinkIcon className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-background p-7 space-y-4 flex flex-col">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">
                Business and Operations
              </p>
              <h3 className="text-xl font-bold">The High Performance MSP</h3>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                This book is written specifically for MSP business owners. It applies
                high-performance thinking to the real challenges MSP operators face: pricing, sales,
                leadership, operations, and building a business that doesn't depend entirely on you.
                The patterns measured in High Performer OS show up in all of it.
              </p>
              <a
                href="https://www.amazon.com/dp/B0FMPFX7R2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                View on Amazon
                <ExternalLinkIcon className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: The three assessments */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 text-center">
            The Assessments
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">
            Each assessment answers a specific question.
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto leading-relaxed">
            Most high performers already sense that something is creating friction. These
            assessments help them understand where it is actually coming from.
          </p>
          <div className="space-y-6">
            {[
              {
                href: routes.assessment('bottleneck'),
                label: 'Performance Bottleneck',
                question: 'Why do I know what to do but still not do it?',
                desc: 'Identifies the internal performance friction affecting execution, consistency, clarity, recovery, and follow-through. This is the first assessment most people take.',
              },
              {
                href: routes.assessment('work_fit'),
                label: 'Work Fit',
                question: 'Is this me, or is this environment draining me?',
                desc: 'Helps identify whether role, culture, pace, values, decision space, and manager fit support how someone performs best. The answer changes what to work on next.',
              },
              {
                href: routes.assessment('perspective'),
                label: 'Perspective',
                question: 'Why does my mind keep interpreting things this way?',
                desc: 'Identifies the interpretive lens someone tends to use under pressure and connects to mindset, self-talk, belief testing, and replacing old operating patterns.',
              },
            ].map(({ href, label, question, desc }) => (
              <div
                key={label}
                className="rounded-2xl border border-border bg-card p-7 flex flex-col sm:flex-row gap-6 items-start"
              >
                <div className="flex-1 space-y-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-primary">{label}</p>
                  <p className="font-semibold text-lg">{question}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
                </div>
                <Link
                  href={href}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'shrink-0 gap-2 whitespace-nowrap',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Take Free Assessment
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Report vs workbook */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Assessment, Report, and Workbooks
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-10">
            Four distinct steps. Each one does different work.
          </h2>
          <div className="space-y-6">
            {[
              {
                step: '01',
                title: 'The assessment',
                desc: 'Identifies the pattern. Fifty questions, about 7 minutes. You receive your type, scores, and top friction areas at no cost.',
              },
              {
                step: '02',
                title: 'The full report',
                desc: 'Explains what is happening and what it is costing. The report expands on the pattern in depth: what drives it, how it shows up, what stops it, and what a better operating pattern looks like for your specific type.',
              },
              {
                step: '03',
                title: 'The 7-Day workbook',
                desc: 'Interrupts the pattern. Structured daily exercises designed to create awareness and friction against the operating pattern you have identified.',
              },
              {
                step: '04',
                title: 'The 30-Day workbook',
                desc: 'Helps install a more useful operating pattern. A longer practice framework for replacing the friction pattern with a more effective one over time.',
              },
            ].map(({ step, title, desc }) => (
              <div key={step} className="flex gap-5 items-start">
                <div className="text-3xl font-bold text-primary/20 leading-none shrink-0 w-10">
                  {step}
                </div>
                <div>
                  <p className="font-semibold mb-1">{title}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 8: What HPOS is not */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Important to Understand
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-6">What High Performer OS is not</h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            High Performer OS is a personal development and coaching tool. It is not a clinical
            instrument. These distinctions matter.
          </p>
          <div className="space-y-3">
            {[
              'Not a clinical diagnostic tool',
              'Not a mental health assessment',
              'Not a personality label or fixed identity',
              'Not a substitute for therapy or professional mental health support when those are needed',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-muted-foreground shrink-0" />
                <p className="text-muted-foreground text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-sm mt-8 leading-relaxed">
            The patterns High Performer OS identifies are operating patterns, not diagnoses. They
            are not permanent. They are practiced responses that can be interrupted, examined, and
            replaced with more useful ones.
          </p>
        </div>
      </section>

      {/* Section 9: Closing CTA */}
      <section className="bg-card border-t border-border py-24 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Recognition is the first step.{' '}
            <span className="text-primary">Now find the pattern.</span>
          </h2>
          <p className="text-muted-foreground mb-10 leading-relaxed">
            The assessments are free. Results are immediate. The pattern you find is the starting
            point for everything that comes after.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href={routes.assessment('bottleneck')}
              className={cn(
                buttonVariants({ size: 'lg' }),
                'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(245,184,0,0.25)] gap-2',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              Start the Free Assessment
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://www.craigwillard.com"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ size: 'lg', variant: 'outline' }),
                'text-base px-8 py-6 h-auto gap-2',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              Visit CraigWillard.com
              <ExternalLinkIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
